import React, { useState } from 'react';
import Quiz from './Quiz';
import MockExam from './MockExam';
import {
  getAllCategories,
  getQuestionsByCategory,
  getRandomQuestions,
  getTotalQuestionCount
} from '../practiceQuestions';

function PracticeMode({ onExit, onBackToSIFISO }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [questionCount, setQuestionCount] = useState(20);
  const [practiceQuestions, setPracticeQuestions] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [showMockExam, setShowMockExam] = useState(false);

  const categories = getAllCategories();
  const totalQuestions = getTotalQuestionCount();

  // If showing mock exam
  if (showMockExam) {
    return (
      <MockExam
        onExit={() => setShowMockExam(false)}
        onBackToSIFISO={onBackToSIFISO}
      />
    );
  }

  const handleStartPractice = (category, count) => {
    let questions;
    if (category === 'random') {
      questions = getRandomQuestions(count);
    } else {
      const categoryQuestions = getQuestionsByCategory(category);
      // Shuffle and take requested count
      questions = categoryQuestions
        .sort(() => 0.5 - Math.random())
        .slice(0, Math.min(count, categoryQuestions.length));
    }

    setPracticeQuestions(questions);
  };

  const handlePracticeComplete = (passed, score, total) => {
    setShowResults(true);
  };

  const handleRetry = () => {
    setPracticeQuestions(null);
    setShowResults(false);
    setSelectedCategory(null);
  };

  // If practicing, show quiz
  if (practiceQuestions && !showResults) {
    return (
      <div className="max-w-4xl mx-auto">
        <Quiz
          questions={practiceQuestions}
          onComplete={handlePracticeComplete}
          levelId="practice"
          isPracticeMode={true}
        />
      </div>
    );
  }

  // If showing results
  if (showResults) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-slate-800 rounded-lg p-8 shadow-lg text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Practice Complete!
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Great job practicing! Keep reinforcing your Python knowledge.
          </p>

          <div className="flex gap-4 justify-center">
            <button
              onClick={handleRetry}
              className="px-6 py-3 rounded-lg font-semibold text-white transition"
              style={{ background: '#008080' }}
            >
              Practice Again
            </button>
            <button
              onClick={onExit}
              className="px-6 py-3 rounded-lg font-semibold text-white transition"
              style={{ background: '#475569' }}
            >
              Back to Levels
            </button>
            <button
              onClick={onBackToSIFISO}
              className="px-6 py-3 rounded-lg font-semibold text-white transition"
              style={{ background: '#475569' }}
            >
              🏠 SIFISO
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Category selection screen
  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-4xl font-bold text-white">
            🎯 Practice Mode
          </h1>
          <div className="flex gap-2">
            <button
              onClick={onExit}
              className="px-4 py-2 rounded-lg transition text-white text-sm"
              style={{ background: '#008080' }}
            >
              ← Back to Levels
            </button>
            <button
              onClick={onBackToSIFISO}
              className="px-4 py-2 rounded-lg transition text-white text-sm"
              style={{ background: '#475569' }}
            >
              🏠 SIFISO
            </button>
          </div>
        </div>
        <p className="text-slate-300 text-lg">
          Practice with {totalQuestions} PCEP-aligned questions organized by syllabus blocks.
          Master Python fundamentals through targeted practice!
        </p>
      </div>

      {/* Mock Exam Section */}
      <div className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 rounded-lg p-6 mb-6 border border-green-500/30">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-white mb-2 flex items-center">
              📝 PCEP Mock Examination
            </h3>
            <p className="text-slate-300 mb-4">
              Take a full certification practice exam. 30 questions, 40-minute timer, real exam conditions.
              Aim for 70%+ to be ready for the actual PCEP certification!
            </p>
            <div className="flex items-center gap-4 text-sm text-slate-300">
              <span>⏰ 40 minutes</span>
              <span>📋 30 questions</span>
              <span>🎯 70% to pass</span>
            </div>
          </div>
          <button
            onClick={() => setShowMockExam(true)}
            className="px-6 py-3 rounded-lg font-semibold bg-green-600 hover:bg-green-700 text-white transition whitespace-nowrap"
          >
            Start Mock Exam →
          </button>
        </div>
      </div>

      {/* Random Practice Option */}
      <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-lg p-6 mb-6 border border-purple-500/30">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-white mb-2 flex items-center">
              🎲 Random Practice
            </h3>
            <p className="text-slate-300 mb-4">
              Get a mix of questions from all PCEP blocks. Great for comprehensive review!
            </p>
            <div className="flex items-center gap-4">
              <label className="text-white font-semibold">
                Number of questions:
              </label>
              <select
                value={questionCount}
                onChange={(e) => setQuestionCount(Number(e.target.value))}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white border border-slate-600 focus:outline-none focus:border-blue-500"
              >
                <option value={10}>10 questions</option>
                <option value={20}>20 questions</option>
                <option value={30}>30 questions</option>
                <option value={40}>40 questions</option>
                <option value={50}>50 questions</option>
              </select>
            </div>
          </div>
          <button
            onClick={() => handleStartPractice('random', questionCount)}
            className="px-6 py-3 rounded-lg font-semibold bg-purple-600 hover:bg-purple-700 text-white transition whitespace-nowrap"
          >
            Start Random Practice →
          </button>
        </div>
      </div>

      {/* PCEP Blocks */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-4">
          📚 Practice by PCEP Syllabus Block
        </h2>
        <p className="text-slate-400 mb-6">
          Focus on specific topics aligned with the official PCEP certification syllabus.
        </p>
      </div>

      {/* Category Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {categories.map((category) => (
          <div
            key={category.id}
            className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-blue-500 transition"
          >
            <h3 className="text-xl font-bold text-white mb-2">
              {category.title}
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              {category.description}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-slate-500 text-sm">
                {category.questionCount} questions available
              </span>
              <button
                onClick={() => {
                  setSelectedCategory(category.id);
                  handleStartPractice(category.id, Math.min(20, category.questionCount));
                }}
                className="px-4 py-2 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white transition text-sm"
              >
                Practice →
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Tips Section */}
      <div className="mt-8 bg-blue-900/30 border-l-4 border-blue-500 p-6 rounded">
        <h3 className="text-lg font-semibold text-blue-200 mb-3">
          💡 Practice Tips
        </h3>
        <ul className="text-blue-100 space-y-2 text-sm">
          <li className="flex items-start">
            <span className="text-blue-400 mr-2">✓</span>
            <span>Start with random practice to identify weak areas</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-400 mr-2">✓</span>
            <span>Focus on specific blocks where you need more practice</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-400 mr-2">✓</span>
            <span>Read explanations carefully to understand concepts</span>
          </li>
          <li className="flex items-start">
            <span className="text-blue-400 mr-2">✓</span>
            <span>Aim for consistent 70%+ scores before taking the mock exam</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default PracticeMode;
